import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ConfigProvider } from 'antd';
import EditarServidor from './pages/EditarServidor';
import AgregarServidor from './pages/AgregarServidor';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Lista from './pages/Lista';
import Clientes from './pages/Clientes';
import Proyectos from './pages/Proyectos';
import Servicios from './pages/Servicios';
import HistorialCargas from './pages/HistorialCargas';
import MetricasServidor from './pages/MetricasServidor';
import MonitorMySQL from './pages/MonitorMySQL';
import ProyectoDetalle from './pages/ProyectoDetalle';
import ProyectoInfra from './pages/ProyectoInfra';
import MonitorUrl from './pages/MonitorUrl';
import MonitorSsl from './pages/MonitorSsl';
import MonitorDominios from './pages/MonitorDominios';
import Alertas from './pages/Alertas';
import Bitacora from './pages/Bitacora';
import Reportes from './pages/Reportes';
import Infraestructura from './pages/Infraestructura';
import MapaInfraestructura from './pages/MapaInfraestructura';
import AdminSMTP from './pages/AdminSMTP';
import OllamaMonitor from './pages/OllamaMonitor';
import ServerProcesses from './pages/ServerProcesses';
import ErrorBoundary from './components/ErrorBoundary';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import { selectIsAuthenticated } from './store/authSlice';
import { logOut } from './store/authSlice';
import './App.css';
import './styles/dashforge-theme.css';

const antTheme = {
  token: {
    colorPrimary: '#0168fa',
    colorInfo: '#0168fa',
    colorLink: '#0168fa',
    colorSuccess: '#10b759',
    colorWarning: '#ffc107',
    colorError: '#dc3545',
    colorTextBase: '#001737',
    colorText: '#001737',
    colorTextSecondary: '#8392a5',
    colorBgBase: '#ffffff',
    colorBgLayout: '#f5f6fa',
    colorBgContainer: '#ffffff',
    colorBorder: '#c0ccda',
    colorBorderSecondary: '#e5e9f2',
    colorSplit: '#e5e9f2',
    borderRadius: 4,
    fontSize: 14,
    fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    controlHeight: 38,
  },
  components: {
    Layout: {
      bodyBg: '#f5f6fa',
      headerBg: '#ffffff',
      siderBg: '#ffffff',
    },
    Card: {
      borderRadiusLG: 4,
      headerBg: 'transparent',
      colorBorderSecondary: '#e5e9f2',
    },
    Table: {
      headerBg: '#f5f6fa',
      headerColor: '#1b2e4b',
      rowHoverBg: '#f4f5f8',
      borderColor: '#e5e9f2',
      headerSplitColor: '#e5e9f2',
    },
    Button: {
      borderRadius: 4,
      primaryShadow: 'none',
      defaultShadow: 'none',
    },
    Tabs: {
      itemSelectedColor: '#0168fa',
      inkBarColor: '#0168fa',
      itemHoverColor: '#0168fa',
    },
    Menu: {
      itemBg: '#ffffff',
      itemColor: '#596882',
      itemHoverBg: '#f5f6fa',
      itemSelectedBg: 'rgba(1, 104, 250, 0.08)',
      itemSelectedColor: '#0168fa',
    },
    Input: {
      activeBorderColor: '#0168fa',
      hoverBorderColor: '#0168fa',
      activeShadow: '0 0 0 2px rgba(1, 104, 250, 0.1)',
    },
    Select: {
      optionSelectedBg: '#e6f0ff',
    },
  },
};

const AppLayout = ({ onLogout }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className={collapsed ? 'sidebar-collapsed' : ''}>
      {mobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
      <Sidebar
        collapsed={collapsed}
        onCollapse={setCollapsed}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
        onLogout={onLogout}
      />
      <Header onMobileMenuToggle={() => setMobileOpen((v) => !v)} />
      <div className={`main-layout ${collapsed ? 'sidebar-collapsed' : ''}`}>
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/lista" element={<Lista />} />
            <Route path="/clientes" element={<Clientes />} />
            <Route path="/proyectos" element={<Proyectos />} />
            <Route path="/proyecto" element={<ProyectoInfra />} />
            <Route path="/servicios" element={<Servicios />} />
            <Route path="/historial" element={<HistorialCargas />} />
            <Route path="/metricas" element={<MetricasServidor />} />
            <Route path="/mysql" element={<MonitorMySQL />} />
            <Route path="/proyecto/:id" element={<ProyectoDetalle />} />
            <Route path="/url-monitor" element={<MonitorUrl />} />
            <Route path="/ssl-monitor" element={<MonitorSsl />} />
            <Route path="/dominios" element={<MonitorDominios />} />
            <Route path="/alertas" element={<Alertas />} />
            <Route path="/bitacora" element={<Bitacora />} />
            <Route path="/reportes" element={<Reportes />} />
            <Route path="/infraestructura" element={<Infraestructura />} />
            <Route path="/mapa-infra" element={<MapaInfraestructura />} />
            <Route path="/admin/smtp" element={<AdminSMTP />} />
            <Route path="/ollama" element={<OllamaMonitor />} />
            <Route path="/procesos" element={<ServerProcesses />} />
            <Route path="/editar/:id" element={<EditarServidor />} />
            <Route path="/agregar" element={<AgregarServidor />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ErrorBoundary>
      </div>
    </div>
  );
};

function App() {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const dispatch = useDispatch();

  const handleLogout = () => {
    dispatch(logOut());
  };

  if (!isAuthenticated) {
    return (
      <ConfigProvider theme={antTheme}>
        <Login />
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={antTheme}>
      <BrowserRouter>
        <AppLayout onLogout={handleLogout} />
      </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;
